import { useState, useEffect, useCallback, useRef } from 'react';
import { ResponsiveGridLayout, useContainerWidth } from 'react-grid-layout';
import { dashboardsAPI, reportsAPI } from '../lib/api';
import { BarChart, Bar, LineChart, Line, PieChart as RechartsPie, Pie, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Loader2, GripVertical, BarChart3, PieChart, TrendingUp, LayoutDashboard, Settings, X, Hash } from 'lucide-react';
import AppShell from '../components/AppShell';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#6366f1', '#14b8a6'];

export default function DashboardBuilderPage() {
  const [dashboards, setDashboards] = useState([]);
  const [activeDashId, setActiveDashId] = useState(null);
  const [activeDash, setActiveDash] = useState(null);
  const [savedReports, setSavedReports] = useState([]);
  const [schema, setSchema] = useState({});
  const [widgetDataMap, setWidgetDataMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddWidget, setShowAddWidget] = useState(false);
  const [showNewDash, setShowNewDash] = useState(false);
  const [newDashName, setNewDashName] = useState('');
  // Add widget form
  const [wReportId, setWReportId] = useState('');
  const [wChartType, setWChartType] = useState('bar');
  const [wXField, setWXField] = useState('');
  const [wYField, setWYField] = useState('_count');
  const [wTitle, setWTitle] = useState('');

  // Load dashboards, saved reports, schema
  useEffect(() => {
    const init = async () => {
      try {
        const [dRes, rRes, sRes] = await Promise.all([
          dashboardsAPI.list(),
          reportsAPI.listSaved(),
          reportsAPI.getSchema(),
        ]);
        setDashboards(dRes.data || []);
        setSavedReports(rRes.data || []);
        setSchema(sRes.data?.sources || {});
        if (dRes.data?.length > 0) setActiveDashId(dRes.data[0].id);
      } catch { /* ignore */ }
      setLoading(false);
    };
    init();
  }, []);

  // Load active dashboard
  useEffect(() => {
    if (!activeDashId) { setActiveDash(null); return; }
    dashboardsAPI.get(activeDashId).then(r => setActiveDash(r.data)).catch(() => {});
  }, [activeDashId]);

  // Fetch widget data
  const fetchWidgetData = useCallback(async (widgets) => {
    if (!activeDashId || !widgets?.length) return;
    const results = {};
    await Promise.all(widgets.map(async (w) => {
      try {
        const res = await dashboardsAPI.widgetData(activeDashId, {
          source: w.source, columns: w.columns, filters: w.filters || {},
        });
        results[w.id] = res.data?.rows || [];
      } catch { results[w.id] = []; }
    }));
    setWidgetDataMap(prev => ({ ...prev, ...results }));
  }, [activeDashId]);

  useEffect(() => {
    if (activeDash?.widgets) fetchWidgetData(activeDash.widgets);
  }, [activeDash, fetchWidgetData]);

  const handleCreateDash = async () => {
    if (!newDashName.trim()) return;
    try {
      const res = await dashboardsAPI.create({ name: newDashName });
      setDashboards(prev => [res.data, ...prev]);
      setActiveDashId(res.data.id);
      setShowNewDash(false);
      setNewDashName('');
      toast.success('Dashboard created');
    } catch { toast.error('Failed to create dashboard'); }
  };

  const handleDeleteDash = async () => {
    if (!activeDashId) return;
    try {
      await dashboardsAPI.remove(activeDashId);
      setDashboards(prev => prev.filter(d => d.id !== activeDashId));
      setActiveDashId(dashboards.find(d => d.id !== activeDashId)?.id || null);
      toast.success('Dashboard deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleSave = async () => {
    if (!activeDash) return;
    setSaving(true);
    try {
      await dashboardsAPI.update(activeDashId, { widgets: activeDash.widgets, name: activeDash.name });
      toast.success('Dashboard saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const handleLayoutChange = (layout) => {
    if (!activeDash) return;
    const updatedWidgets = activeDash.widgets.map(w => {
      const l = layout.find(li => li.i === w.id);
      if (l) return { ...w, x: l.x, y: l.y, w: l.w, h: l.h };
      return w;
    });
    setActiveDash(prev => ({ ...prev, widgets: updatedWidgets }));
  };

  const handleAddWidget = async () => {
    if (!wReportId || !activeDash) return;
    const report = savedReports.find(r => r.id === wReportId);
    if (!report) return;
    const src = schema[report.source];
    const allFields = src?.fields || {};
    const xField = wXField || Object.keys(allFields).find(k => allFields[k].type === 'string') || '';

    const widget = {
      id: 'w-' + Date.now(),
      report_id: wReportId,
      title: wTitle || report.name,
      source: report.source,
      columns: report.columns,
      filters: report.filters || {},
      chart_type: wChartType,
      x_field: xField,
      y_field: wYField || '_count',
      x: 0, y: Infinity, w: 4, h: 3,
    };
    const newWidgets = [...activeDash.widgets, widget];
    setActiveDash(prev => ({ ...prev, widgets: newWidgets }));
    setShowAddWidget(false);
    setWReportId(''); setWTitle(''); setWXField(''); setWYField('_count'); setWChartType('bar');

    // Fetch data for the new widget
    try {
      const res = await dashboardsAPI.widgetData(activeDashId, {
        source: widget.source, columns: widget.columns, filters: widget.filters,
      });
      setWidgetDataMap(prev => ({ ...prev, [widget.id]: res.data?.rows || [] }));
    } catch { /* ignore */ }
  };

  const handleRemoveWidget = (widgetId) => {
    if (!activeDash) return;
    setActiveDash(prev => ({ ...prev, widgets: prev.widgets.filter(w => w.id !== widgetId) }));
  };

  const selectedReport = savedReports.find(r => r.id === wReportId);
  const selectedSource = selectedReport ? schema[selectedReport.source] : null;
  const selectedFields = selectedSource?.fields || {};

  if (loading) {
    return <AppShell><div className="flex items-center justify-center h-[calc(100vh-4rem)]"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--brand))]" /></div></AppShell>;
  }

  return (
    <AppShell>
      <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="dashboard-builder">
        {/* Top Bar */}
        <div className="border-b p-4 flex items-center gap-3 bg-card">
          <LayoutDashboard className="w-5 h-5 text-[hsl(var(--brand))]" />
          <div className="flex-1">
            <h1 className="text-lg font-semibold" style={{ fontFamily: 'DM Sans' }}>Dashboards</h1>
          </div>

          {/* Dashboard Tabs */}
          <div className="flex items-center gap-1 border rounded-md px-1 py-0.5">
            {dashboards.map(d => (
              <button key={d.id} onClick={() => setActiveDashId(d.id)}
                className={`px-3 py-1 text-xs rounded transition-colors ${activeDashId === d.id ? 'bg-[hsl(var(--brand))] text-white' : 'hover:bg-accent'}`}
                data-testid={`dash-tab-${d.id}`}>
                {d.name}
              </button>
            ))}
            <button onClick={() => setShowNewDash(true)} className="px-2 py-1 text-xs hover:bg-accent rounded" data-testid="new-dash-btn">
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="h-6 w-px bg-border" />
          <Button size="sm" variant="outline" onClick={() => setShowAddWidget(true)} disabled={!activeDash} data-testid="add-widget-btn">
            <Plus className="w-4 h-4 mr-1" />Add Widget
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!activeDash || saving} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="save-dash-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}Save
          </Button>
          {activeDash && (
            <Button size="sm" variant="ghost" className="text-destructive" onClick={handleDeleteDash} data-testid="delete-dash-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-auto p-4 bg-background">
          {!activeDash ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-4">
              <LayoutDashboard className="w-16 h-16 opacity-20" />
              <p>Create a dashboard to get started</p>
              <Button onClick={() => setShowNewDash(true)} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="create-first-dash-btn">
                <Plus className="w-4 h-4 mr-1" />New Dashboard
              </Button>
            </div>
          ) : activeDash.widgets?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-4">
              <BarChart3 className="w-16 h-16 opacity-20" />
              <p>No widgets yet — add one from your saved reports</p>
              <Button onClick={() => setShowAddWidget(true)} variant="outline" data-testid="add-first-widget-btn">
                <Plus className="w-4 h-4 mr-1" />Add Widget
              </Button>
            </div>
          ) : (
            <DashboardGrid widgets={activeDash.widgets} onLayoutChange={handleLayoutChange} onRemoveWidget={handleRemoveWidget} widgetDataMap={widgetDataMap} schema={schema} />
          )}
        </div>

        {/* New Dashboard Dialog */}
        <Dialog open={showNewDash} onOpenChange={setShowNewDash}>
          <DialogContent className="max-w-sm" data-testid="new-dash-dialog">
            <DialogHeader><DialogTitle>New Dashboard</DialogTitle></DialogHeader>
            <div className="py-4">
              <Label className="mb-2 block">Dashboard Name</Label>
              <Input value={newDashName} onChange={e => setNewDashName(e.target.value)} placeholder="e.g. Operations Overview" data-testid="new-dash-name" autoFocus />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewDash(false)}>Cancel</Button>
              <Button onClick={handleCreateDash} disabled={!newDashName.trim()} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="confirm-new-dash-btn">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Widget Dialog */}
        <Dialog open={showAddWidget} onOpenChange={setShowAddWidget}>
          <DialogContent className="max-w-md" data-testid="add-widget-dialog">
            <DialogHeader><DialogTitle>Add Widget</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="mb-1.5 block text-xs">Saved Report</Label>
                <Select value={wReportId} onValueChange={v => { setWReportId(v); setWXField(''); setWYField('_count'); const r = savedReports.find(x=>x.id===v); if(r) setWTitle(r.name); }}>
                  <SelectTrigger data-testid="widget-report-select"><SelectValue placeholder="Select a saved report" /></SelectTrigger>
                  <SelectContent>
                    {savedReports.map(r => <SelectItem key={r.id} value={r.id}>{r.name} ({schema[r.source]?.label || r.source})</SelectItem>)}
                  </SelectContent>
                </Select>
                {savedReports.length === 0 && <p className="text-xs text-muted-foreground mt-1">No saved reports — save one in the Report Builder first</p>}
              </div>
              <div>
                <Label className="mb-1.5 block text-xs">Widget Title</Label>
                <Input value={wTitle} onChange={e => setWTitle(e.target.value)} placeholder="Chart title" data-testid="widget-title-input" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="mb-1.5 block text-xs">Chart Type</Label>
                  <Select value={wChartType} onValueChange={setWChartType}>
                    <SelectTrigger data-testid="widget-chart-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bar">Bar</SelectItem>
                      <SelectItem value="line">Line</SelectItem>
                      <SelectItem value="area">Area</SelectItem>
                      <SelectItem value="pie">Pie</SelectItem>
                      <SelectItem value="metric">Metric</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs">X-Axis</Label>
                  <Select value={wXField} onValueChange={setWXField} disabled={!selectedReport}>
                    <SelectTrigger data-testid="widget-x-field"><SelectValue placeholder="Auto" /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(selectedFields).filter(([,f]) => f.type === 'string' || f.type === 'boolean').map(([k, f]) => (
                        <SelectItem key={k} value={k}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1.5 block text-xs">Y-Axis</Label>
                  <Select value={wYField} onValueChange={setWYField} disabled={!selectedReport}>
                    <SelectTrigger data-testid="widget-y-field"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_count">Count</SelectItem>
                      {Object.entries(selectedFields).filter(([,f]) => f.type === 'number').map(([k, f]) => (
                        <SelectItem key={k} value={k}>{f.label} (sum)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddWidget(false)}>Cancel</Button>
              <Button onClick={handleAddWidget} disabled={!wReportId} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="confirm-add-widget-btn">Add Widget</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}

// ─── Dashboard Grid (uses v2 useContainerWidth) ───
function DashboardGrid({ widgets, onLayoutChange, onRemoveWidget, widgetDataMap, schema }) {
  const { width, containerRef, mounted } = useContainerWidth({ initialWidth: 1200 });

  const layout = widgets.map(w => ({
    i: w.id, x: w.x, y: w.y, w: w.w || 4, h: w.h || 3, minW: 2, minH: 2,
  }));

  if (!mounted) return <div ref={containerRef} style={{ width: '100%' }} />;

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <ResponsiveGridLayout
        width={width}
        layouts={{ lg: layout, md: layout, sm: layout }}
        breakpoints={{ lg: 1200, md: 996, sm: 768 }}
        cols={{ lg: 12, md: 8, sm: 6 }}
        rowHeight={100}
        onLayoutChange={(newLayout) => onLayoutChange(newLayout)}
        draggableHandle=".widget-drag-handle"
        isResizable={true}
      >
        {widgets.map(w => (
          <div key={w.id} className="rounded-lg border bg-card shadow-sm overflow-hidden flex flex-col" data-testid={`widget-${w.id}`}>
            <div className="flex items-center gap-2 px-3 py-2 border-b bg-card/80">
              <GripVertical className="w-3.5 h-3.5 text-muted-foreground cursor-grab widget-drag-handle" />
              <span className="text-xs font-medium truncate flex-1">{w.title}</span>
              <Badge variant="outline" className="text-[9px] shrink-0">{w.chart_type}</Badge>
              <button onClick={() => onRemoveWidget(w.id)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`remove-widget-${w.id}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex-1 p-2 min-h-0">
              <WidgetChart rows={widgetDataMap[w.id] || []} config={w} fields={schema[w.source]?.fields || {}} />
            </div>
          </div>
        ))}
      </ResponsiveGridLayout>
    </div>
  );
}

// ─── Widget Chart Renderer ───
function WidgetChart({ rows, config, fields }) {
  if (!rows || rows.length === 0) {
    return <div className="flex items-center justify-center h-full text-muted-foreground text-xs"><Loader2 className="w-4 h-4 animate-spin mr-1" />Loading...</div>;
  }

  const { chart_type, x_field, y_field } = config;

  // Metric mode — show single aggregated number
  if (chart_type === 'metric') {
    const val = y_field === '_count' ? rows.length : rows.reduce((s, r) => s + (Number(r[y_field]) || 0), 0);
    const label = y_field === '_count' ? 'Total Records' : (fields[y_field]?.label || y_field);
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Hash className="w-6 h-6 text-[hsl(var(--brand))] mb-2 opacity-40" />
        <span className="text-3xl font-bold tabular-nums">{val.toLocaleString()}</span>
        <span className="text-xs text-muted-foreground mt-1">{label}</span>
      </div>
    );
  }

  // Aggregate
  const xKey = x_field || Object.keys(fields).find(k => fields[k]?.type === 'string') || '';
  const yKey = y_field || '_count';
  const groups = {};
  rows.forEach(r => {
    const name = String(r[xKey] ?? 'Unknown');
    if (!groups[name]) groups[name] = { name, _count: 0 };
    groups[name]._count++;
    if (yKey !== '_count' && r[yKey] !== undefined) groups[name][yKey] = (groups[name][yKey] || 0) + (Number(r[yKey]) || 0);
  });
  const data = Object.values(groups).sort((a, b) => (b[yKey] || b._count) - (a[yKey] || a._count)).slice(0, 15);

  const tipStyle = { contentStyle: { background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '6px', fontSize: '11px' } };

  if (chart_type === 'pie') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <RechartsPie>
          <Pie data={data} dataKey={yKey} nameKey="name" cx="50%" cy="50%" outerRadius="75%" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip {...tipStyle} />
        </RechartsPie>
      </ResponsiveContainer>
    );
  }

  const Comp = chart_type === 'line' ? LineChart : chart_type === 'area' ? AreaChart : BarChart;
  const Data = chart_type === 'line' ? Line : chart_type === 'area' ? Area : Bar;
  const extra = chart_type === 'area' ? { fillOpacity: 0.3 } : {};

  return (
    <ResponsiveContainer width="100%" height="100%">
      <Comp data={data} margin={{ top: 5, right: 10, left: 0, bottom: 30 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
        <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-25} textAnchor="end" interval={0} />
        <YAxis tick={{ fontSize: 9 }} width={35} />
        <Tooltip {...tipStyle} />
        <Data dataKey={yKey} fill={COLORS[0]} stroke={COLORS[0]} {...extra} />
      </Comp>
    </ResponsiveContainer>
  );
}
